import { useEffect, useId, useState } from 'react';

import type { PageAccessLevel } from '@noti/shared';

import type { PageSharing, SharePerson } from '../api';
import { fetchPageSharing, removePageShare, setPageGeneralAccess, setPageShare } from '../api';
import type { MessageKey } from '../i18n';
import { t } from '../i18n';
import { useToast } from './Toast';

/** The four levels a person can get, strongest first, as in the prototype. */
const LEVELS: { value: PageAccessLevel; label: MessageKey }[] = [
  { value: 'edit', label: 'share.level.edit' },
  { value: 'comment', label: 'share.level.comment' },
  { value: 'view', label: 'share.level.view' },
];

/** Which sentence belongs to each general access choice. */
const NOTES: Record<PageSharing['general'], MessageKey> = {
  invited: 'share.note.invited',
  workspace: 'share.note.workspace',
  web: 'share.note.web',
};

const GENERAL: { value: PageSharing['general']; label: MessageKey }[] = [
  { value: 'invited', label: 'share.general.invited' },
  { value: 'workspace', label: 'share.general.workspace' },
  { value: 'web', label: 'share.general.web' },
];

interface ShareDialogProps {
  pageId: string;
  /** Page title with its icon, the way the prototype puts it in the heading. */
  title: string;
  /** Public link of the page, offered as Copy link. */
  link: string;
  onClose: () => void;
  onInviteMembers: () => void;
}

/** Share one page: people, their level, and how anyone at all can open it. */
export function ShareDialog({
  pageId,
  title,
  link,
  onClose,
  onInviteMembers,
}: ShareDialogProps): React.ReactElement {
  const titleId = useId();
  const toast = useToast();
  const [sharing, setSharing] = useState<PageSharing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [level, setLevel] = useState<PageAccessLevel>('edit');
  const [tab, setTab] = useState<'share' | 'publish'>('share');

  useEffect(() => {
    let live = true;
    void fetchPageSharing(pageId)
      .then((loaded) => {
        if (live) setSharing(loaded);
      })
      .catch(() => {
        if (live) setError(t('error.generic'));
      });
    return () => {
      live = false;
    };
  }, [pageId]);

  const invite = (): void => {
    const address = email.trim().toLowerCase();
    if (!address || !sharing) return;
    void setPageShare(pageId, { type: 'email', id: address }, level)
      .then(() => fetchPageSharing(pageId))
      .then((loaded) => {
        setSharing(loaded);
        setEmail('');
        toast(t('share.invited', { email: address }));
      })
      .catch(() => setError(t('share.alreadyHasAccess')));
  };

  const changeLevel = (person: SharePerson, next: PageAccessLevel): void => {
    if (!sharing) return;
    setSharing({
      ...sharing,
      people: sharing.people.map((entry) => (entry === person ? { ...entry, level: next } : entry)),
    });
    void setPageShare(pageId, { type: person.principalType, id: person.principalId }, next).catch(
      () => setError(t('error.generic')),
    );
  };

  const remove = (person: SharePerson): void => {
    if (!sharing) return;
    setSharing({ ...sharing, people: sharing.people.filter((entry) => entry !== person) });
    void removePageShare(pageId, { type: person.principalType, id: person.principalId }).catch(() =>
      setError(t('error.generic')),
    );
  };

  return (
    <div
      className="overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="share"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
      >
        <div className="share__head">
          <h2 className="share__title" id={titleId}>
            {t('share.heading', { title })}
          </h2>
          <div className="share__tabs">
            <button
              type="button"
              className={tab === 'share' ? 'share__tab nb is-on' : 'share__tab nb'}
              aria-pressed={tab === 'share'}
              onClick={() => setTab('share')}
            >
              {t('share.tab.share')}
            </button>
            <button
              type="button"
              className={tab === 'publish' ? 'share__tab nb is-on' : 'share__tab nb'}
              aria-pressed={tab === 'publish'}
              onClick={() => setTab('publish')}
            >
              {t('share.tab.publish')}
            </button>
          </div>
        </div>

        {error ? (
          <p className="share__error" role="alert">
            {error}
          </p>
        ) : null}

        {tab === 'publish' ? (
          <p className="share__empty">{t('share.publishNotBuilt')}</p>
        ) : (
          <div className="share__body">
            <form
              className="share__invite"
              onSubmit={(event) => {
                event.preventDefault();
                invite();
              }}
            >
              <label className="share__searchwrap">
                <span className="sr-only">{t('share.searchPeople')}</span>
                <input
                  className="ni share__search"
                  type="email"
                  autoComplete="off"
                  placeholder={t('share.searchPlaceholder')}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </label>
              <label className="share__levelwrap">
                <span className="sr-only">{t('share.levelLabel')}</span>
                <select
                  className="ni share__level"
                  value={level}
                  onChange={(event) => setLevel(event.target.value as PageAccessLevel)}
                >
                  {LEVELS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.label)}
                    </option>
                  ))}
                </select>
              </label>
              <button type="submit" className="nb share__invitebtn">
                {t('share.invite')}
              </button>
            </form>

            <div className="share__eyebrow">{t('share.peopleWithAccess')}</div>
            {sharing ? (
              <ul className="share__people">
                {sharing.people.map((person) => (
                  <li
                    className="share__person"
                    key={`${person.principalType}:${person.principalId}`}
                  >
                    <span className="share__avatar" aria-hidden="true">
                      {initialsOf(person.name)}
                    </span>
                    <span className="share__persontext">
                      <span className="share__personname">{person.name}</span>
                      <span className="share__personmail">{person.email}</span>
                    </span>
                    {person.level ? (
                      <select
                        className="ni share__personlevel"
                        aria-label={t('share.accessFor', { name: person.name })}
                        value={person.level}
                        onChange={(event) =>
                          changeLevel(person, event.target.value as PageAccessLevel)
                        }
                      >
                        {LEVELS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(option.label)}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    <button
                      type="button"
                      className="nb share__remove"
                      aria-label={t('share.remove', { name: person.name })}
                      onClick={() => remove(person)}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="share__loading">{t('share.loading')}</p>
            )}

            {sharing?.inheritedFrom ? (
              <p className="share__note">{t('share.inherited', { page: sharing.inheritedFrom })}</p>
            ) : null}

            <div className="share__eyebrow">{t('share.generalAccess')}</div>
            {sharing ? (
              <>
                <label className="share__generalwrap">
                  <span className="sr-only">{t('share.generalAccess')}</span>
                  <select
                    className="ni share__general"
                    value={sharing.general}
                    onChange={(event) => {
                      const next = event.target.value as PageSharing['general'];
                      setSharing({ ...sharing, general: next });
                      void setPageGeneralAccess(pageId, next).catch(() =>
                        setError(t('error.generic')),
                      );
                    }}
                  >
                    {GENERAL.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.label, { workspace: sharing.workspaceName })}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="share__note">
                  {t(NOTES[sharing.general], { workspace: sharing.workspaceName })}
                </p>
              </>
            ) : null}
          </div>
        )}

        <div className="share__foot">
          <button
            type="button"
            className="nb share__secondary"
            onClick={() => void navigator.clipboard.writeText(link)}
          >
            {t('share.copyLink')}
          </button>
          <button type="button" className="nb share__link" onClick={onInviteMembers}>
            {t('share.manageMembers')}
          </button>
          <button type="button" className="nb share__done" onClick={onClose}>
            {t('share.done')}
          </button>
        </div>
      </div>
    </div>
  );
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}
